/**
 * Dev-tools override for the sidebar upgrade nudges.
 *
 * Same shape as the billing override (`./overrides.ts`): a sessionStorage-backed
 * atom. The sidebar card and its launch gate apply it themselves. The shared
 * allowance query stays real, so a preset cannot change other surfaces. The
 * team role is forced only for the nudge; team queries keep the real role.
 *
 * Production safety: both apply functions are identity whenever
 * `getEnvironment()` reports production, read per call.
 */
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import { useAtomValue } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

import type { CreditAllowanceResponse } from '../../contracts';

export type UpgradeNudgesDevTeamRole = 'admin' | 'member';

export type UpgradeNudgesDevOverride = {
  /** Whole-number percent of the plan allowance the forced cycle has used. */
  usagePercent?: number;
  teamRole?: UpgradeNudgesDevTeamRole;
};

/** Forced cycles use a round limit so the percent reads back exactly. */
const DEV_ALLOWANCE_LIMIT = 1000;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const DEV_CYCLE_DAYS = 30;

const sessionStorageAdapter = createJSONStorage<UpgradeNudgesDevOverride | null>(() => {
  if (typeof window === 'undefined') {
    return {
      getItem: () => null,
      setItem: () => undefined,
      removeItem: () => undefined,
    };
  }
  return window.sessionStorage;
});

export const upgradeNudgesDevOverrideAtom =
  atomWithStorage<UpgradeNudgesDevOverride | null>(
    'heidi.devTools.upgradeNudgesOverride',
    null,
    sessionStorageAdapter,
    { getOnInit: true }
  );

const isProduction = (): boolean => getEnvironment() === ENV_NAME.PROD;

/**
 * The allowance with its usage replaced by the forced percent. Keeps the real
 * cycle dates when there are any so threshold memory stays keyed on them, and
 * always returns a capped cycle so complimentary copy never takes over.
 */
export function applyCreditAllowanceDevOverride(
  allowance: CreditAllowanceResponse | undefined,
  override: UpgradeNudgesDevOverride | null,
  now: Date = new Date()
): CreditAllowanceResponse | undefined {
  if (override?.usagePercent == null || isProduction()) {
    return allowance;
  }
  const percent = Math.min(100, Math.max(0, Math.round(override.usagePercent)));
  const consumed = (DEV_ALLOWANCE_LIMIT * percent) / 100;
  const cycleStart =
    allowance?.cycle_start ?? new Date(now.getTime() - MS_PER_DAY).toISOString();
  const cycleEnd =
    allowance?.cycle_end ??
    new Date(now.getTime() + DEV_CYCLE_DAYS * MS_PER_DAY).toISOString();
  return {
    ...allowance,
    limit: String(DEV_ALLOWANCE_LIMIT),
    display_limit: String(DEV_ALLOWANCE_LIMIT),
    consumed: String(consumed),
    remaining: String(DEV_ALLOWANCE_LIMIT - consumed),
    cycle_start: cycleStart,
    cycle_end: cycleEnd,
  };
}

/**
 * Allowance the sidebar card and its launch gate read. Other allowance
 * consumers keep the query result.
 */
export function useDevOverriddenCreditAllowance(
  allowance: CreditAllowanceResponse | null | undefined
): CreditAllowanceResponse | null | undefined {
  const override = useAtomValue(upgradeNudgesDevOverrideAtom);
  return applyCreditAllowanceDevOverride(allowance ?? undefined, override) ?? allowance;
}

/** The forced team role, or `null` when none applies (always in production). */
export function getForcedTeamRoleDevOverride(
  override: UpgradeNudgesDevOverride | null
): UpgradeNudgesDevTeamRole | null {
  if (override?.teamRole == null || isProduction()) {
    return null;
  }
  return override.teamRole;
}
